import { Icon } from '../../components/Icon.tsx';
import { formatDvDate, formatDvTime } from '../../lib/dvDate.ts';
import { formatElapsed, formatBytes } from './RecordingBar.tsx';
import type { RecordingRow } from './api.ts';

// A meeting can hold at most 3 recordings (enforced server-side). Mirrored
// here purely so the UI can explain — via a muted note instead of the record
// pill — why recording is no longer offered once the cap is hit.
const MAX_RECORDINGS = 3;

// Local Dhivehi strings — kept inline (not in i18n/dv.ts) since dv.ts carries
// unrelated in-flight edits on this branch and this task's commit must stay
// scoped to RecordingsCard.tsx + YaumiyyaEditor.tsx.
const STR = {
  title: 'ރެކޯޑިންގތައް',
  processing: 'ޕްރޮސެސްކުރަނީ…',
  play: 'ޕްލޭ',
  empty: 'އަދި ރެކޯޑިންގއެއް ނެތް',
  record: 'ރެކޯޑް ކުރޭ',
  maxReached: 'ގިނަވެގެން 3 ރެކޯޑިން',
  del: 'ޑިލީޓް',
  confirmDelete: 'މި ރެކޯޑިން ޑިލީޓް ކުރަންވީތޯ؟',
};

// One recording row: a small mic-glyph chip, the date+time as the primary
// line, and duration • size as a muted secondary line — mirrors the
// leaderboard/list-row convention used elsewhere (chip + two text lines).
function RecordingItem({ recording, isWriter, onPlay, onDelete }: {
  recording: RecordingRow;
  isWriter: boolean;
  onPlay: () => void;
  onDelete: () => void;
}): JSX.Element {
  const ready = recording.status === 'READY';
  return (
    <li className="flex items-center gap-4 px-4 sm:px-5 py-3.5 transition-colors hover:bg-[hsl(var(--surface-high))]">
      <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
        <Icon name="graphic_eq" size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">
          {formatDvDate(recording.createdAt)} {formatDvTime(recording.createdAt)}
        </p>
        <p className="mono-meta mt-0.5 truncate text-end text-sm text-muted-foreground" dir="ltr">
          {ready
            ? `${recording.durationSec != null ? formatElapsed(recording.durationSec * 1000) : '—'} • ${formatBytes(recording.sizeBytes)}`
            : STR.processing}
        </p>
      </div>
      {ready && (
        <button
          type="button"
          aria-label={STR.play}
          title={STR.play}
          className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90"
          onClick={onPlay}
        >
          <Icon name="play_arrow" size={20} fill />
        </button>
      )}
      {isWriter && (
        <button
          type="button"
          aria-label={STR.del}
          title={STR.del}
          className="grid size-10 shrink-0 place-items-center rounded-full bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))] transition-colors hover:brightness-95"
          onClick={onDelete}
        >
          <Icon name="delete" size={20} />
        </button>
      )}
    </li>
  );
}

// Recordings card — shares the meta-info row (lg:col-span-1) in
// YaumiyyaEditor. h-full + flex column so it matches the meeting-info card's
// height on that row; the list scrolls internally once it grows past ~3 rows
// (max-h caps it) so extra recordings never grow the card. The record pill
// (or, once 3 recordings exist, a max-reached note) is pinned below via
// mt-auto, outside the scroll region.
export function RecordingsCard({ recordings, isWriter, canRecord, onPlay, onRecord, onDelete }: {
  recordings: RecordingRow[];
  isWriter: boolean;
  canRecord: boolean;
  onPlay: (id: string) => void;
  onRecord: () => void;
  onDelete: (id: string) => void;
}): JSX.Element {
  const hasRecordings = recordings.length > 0;
  const atMax = recordings.length >= MAX_RECORDINGS;

  function handleDelete(id: string): void {
    if (window.confirm(STR.confirmDelete)) onDelete(id);
  }

  return (
    <section className="flex h-full flex-col rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
      <h2 className="section-heading">
        <span className="text-lg font-bold">{STR.title}</span>
      </h2>
      {/* Max is 3 recordings per meeting, so the list always fits — no scroll. */}
      <div className="mt-1 flex-1">
        {hasRecordings ? (
          <ul className="-mx-5 divide-y divide-black/[0.05] overflow-hidden lg:-mx-6">
            {recordings.map((r) => (
              <RecordingItem
                key={r.id}
                recording={r}
                isWriter={isWriter}
                onPlay={() => onPlay(r.id)}
                onDelete={() => handleDelete(r.id)}
              />
            ))}
          </ul>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1.5 py-4 text-center">
            <Icon name="graphic_eq" size={28} className="text-muted-foreground/40" />
            <p className="text-xs text-muted-foreground">{STR.empty}</p>
          </div>
        )}
      </div>
      {/* Start-recording pill, or — once the 3-recording cap is hit — a
          muted note explaining why it's gone. Same isWriter + canRecord
          (activeBar === null) gate as before; hidden once a bottom bar
          (recording or playback) is up, since only one may be active at a
          time. mt-auto pins it to the card bottom, below the scroll region. */}
      {isWriter && canRecord && (
        <div className="mt-auto pt-3 text-center">
          <button
            type="button"
            disabled={atMax}
            title={atMax ? STR.maxReached : undefined}
            className="btn-primary mx-auto inline-flex items-center gap-2"
            onClick={onRecord}
          >
            <Icon name="mic" size={18} />
            {STR.record}
          </button>
          {atMax && <p className="mt-1.5 text-xs text-muted-foreground">{STR.maxReached}</p>}
        </div>
      )}
    </section>
  );
}
